import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getIdToken } from './auth';

/**
 * Axios API client configured to communicate with the API Gateway backend.
 *
 * The base URL defaults to the environment variable REACT_APP_API_URL,
 * falling back to '/api' for local development proxying.
 *
 * Authentication tokens are attached automatically via a request interceptor
 * that fetches the current Cognito ID token (refreshing if needed).
 * The ID token is required because the API Gateway Cognito authorizer
 * validates ID tokens (not access tokens) and extracts claims like
 * cognito:groups for role-based access control.
 * A response interceptor handles 401 errors by redirecting to login.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor — attach ID token from Cognito session
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = await getIdToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        // Try to get token from localStorage as fallback
        const storedToken = localStorage.getItem('idToken');
        if (storedToken && config.headers) {
          config.headers.Authorization = `Bearer ${storedToken}`;
        }
      }
    } catch {
      // Try localStorage fallback on error
      const storedToken = localStorage.getItem('idToken');
      if (storedToken && config.headers) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor — handle 401 (expired/invalid token)
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Don't auto-redirect - let the component handle the error
      // This prevents redirect loops when token refresh is in progress
    }
    return Promise.reject(error);
  }
);

export default apiClient;
